import type { CountryCounts, Weather } from '@travel-planner/shared';
import { useDashboard, weatherCodeInfo } from './useDashboard';

function countdownText(daysUntilStart: number): string {
  if (daysUntilStart > 1) return `${daysUntilStart} days to go`;
  if (daysUntilStart === 1) return 'Tomorrow!';
  if (daysUntilStart === 0) return 'Today!';
  return 'In progress / past';
}

export function DashboardHome({ tripId }: { tripId: string }) {
  const { data, isLoading } = useDashboard(tripId);
  if (isLoading || !data) return null;

  return (
    <section className="row g-3 mt-1">
      <div className="col-12 col-sm-6 col-lg-3">
        <Card title="Countdown">
          <div className="fs-3 fw-bold">{countdownText(data.daysUntilStart)}</div>
          <div className="text-secondary small">
            {data.start} → {data.end}
          </div>
        </Card>
      </div>

      <div className="col-12 col-sm-6 col-lg-3">
        <Card title="Open Must items">
          <div className={`fs-3 fw-bold ${data.totalOpenMust ? 'text-danger' : 'text-success'}`}>
            {data.totalOpenMust}
          </div>
          <div className="text-secondary small">across all countries</div>
        </Card>
      </div>

      <div className="col-12 col-sm-6 col-lg-3">
        <WeatherCard weather={data.weather} />
      </div>

      <div className="col-12 col-sm-6 col-lg-3">
        <Card title="Per-country">
          {data.counts.length === 0 ? (
            <div className="text-secondary small">No countries yet.</div>
          ) : (
            <div className="d-flex flex-column gap-1">
              {data.counts.map((c) => (
                <CountryRow key={c.countryId} c={c} />
              ))}
            </div>
          )}
        </Card>
      </div>
    </section>
  );
}

function CountryRow({ c }: { c: CountryCounts }) {
  return (
    <div className="d-flex justify-content-between gap-2 small">
      <span className="fw-semibold">{c.name}</span>
      <span className="text-secondary">
        {c.openMust > 0 && <span className="text-danger">{c.openMust} open must · </span>}
        {c.must}/{c.should}/{c.could} ({c.total})
      </span>
    </div>
  );
}

function WeatherCard({ weather }: { weather: Weather }) {
  if (!weather) {
    return (
      <Card title="Weather">
        <div className="text-secondary small">Add map markers to see destination weather.</div>
      </Card>
    );
  }
  return (
    <Card title="Destination weather">
      <div className="d-flex align-items-baseline gap-2">
        {weather.currentTemp !== null && (
          <span className="fs-3 fw-bold">{Math.round(weather.currentTemp)}°</span>
        )}
        <span className="text-secondary" style={{ fontSize: '0.78rem' }}>
          {weather.timezone}
        </span>
      </div>
      <div className="d-flex gap-2 mt-1">
        {weather.days.slice(0, 5).map((d) => {
          const info = weatherCodeInfo(d.code);
          return (
            <div key={d.date} className="text-center" style={{ fontSize: '0.72rem' }}>
              <div title={info.label}>{info.icon}</div>
              <div className="text-secondary">{d.date.slice(5)}</div>
              <div>
                {d.tMax !== null ? Math.round(d.tMax) : '–'}/{d.tMin !== null ? Math.round(d.tMin) : '–'}°
              </div>
            </div>
          );
        })}
      </div>
      {weather.note && (
        <div className="text-body-tertiary mt-1" style={{ fontSize: '0.72rem' }}>
          {weather.note}
        </div>
      )}
    </Card>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card h-100">
      <div className="card-body p-3">
        <div
          className="text-uppercase text-body-tertiary mb-2"
          style={{ fontSize: '0.7rem', letterSpacing: '0.06em' }}
        >
          {title}
        </div>
        {children}
      </div>
    </div>
  );
}
